'use client'

import { useCallback, useState } from 'react'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { dialogTitleClassName } from '~/components/smart-accounts/TransactionDetailDialogContent'
import { TransactionDisplay } from '~/components/txn-display'
import { useTransactionStateChanges } from '~/components/txn-display/state-change/hooks/useTransactionStateChanges'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { useBatchOperationRemover } from '~/hooks/useBatchOperationRemover'
import { useSmartAccount } from '~/hooks/useSmartAccounts'
import { useOrgTransaction } from '~/hooks/useTransactions'
import { useDialogRoutePath } from '~/hooks/utils'
import { OrgTransaction } from '~/types'

export const BatchTransactionDialogContent = ({
  transaction: transactionProp,
  onClose,
}: {
  transaction: OrgTransaction
  onClose: () => void
}) => {
  // When the user removes the last operation we're about to delete the batch
  // server-side. We detach the observer first so react-query doesn't refetch
  // the soon-to-be-404 id while the close animation is still running.
  const [isDetached, setIsDetached] = useState(false)
  const { transaction: latestTransaction } = useOrgTransaction(
    transactionProp.id,
    { enabled: !isDetached },
  )
  const transaction = latestTransaction ?? transactionProp
  const { smartAccount } = useSmartAccount(transaction.smartAccountAddress)
  const { approveAndSubmitTransaction, isProcessing: isApproving } =
    useApproveAndSubmitTransaction()
  const { isSimulationRevert } = useTransactionStateChanges({
    transaction,
  })

  useDialogRoutePath({
    closeModal: onClose,
    globalModal: true,
    getPath: () => {
      if (smartAccount) {
        return `/accounts/${smartAccount.address}/transactions/${transaction.id}/`
      }
    },
  })

  const {
    handleRemoveOperation,
    removingOperationId,
    isProcessing: isRemovingOrDeleting,
  } = useBatchOperationRemover({
    transaction,
    onBatchEmptied: useCallback(() => {
      setIsDetached(true)
      onClose()
    }, [onClose]),
  })

  const handleSubmit = useCallback(async () => {
    if (!smartAccount) return
    // `approveAndSubmitTransaction` converts drafted batches internally, so a
    // retry after a dismissed passkey prompt goes straight to signing.
    await approveAndSubmitTransaction({
      transaction,
      smartAccount,
      onSubmit: onClose,
    })
  }, [approveAndSubmitTransaction, onClose, smartAccount, transaction])

  return (
    <SplitsDialogContent
      id="batchTransaction"
      title={
        <TransactionTitle
          transaction={transaction}
          className={dialogTitleClassName}
          textWrap
        />
      }
      variant="cta"
      actionButton={{
        text: 'Submit',
        onClick: handleSubmit,
        isDisabled:
          !transaction.batchOperations?.length ||
          isSimulationRevert ||
          !smartAccount,
      }}
      isLoading={isRemovingOrDeleting || isApproving}
      clampTitle={false}
    >
      <TransactionDisplay
        transaction={transaction}
        showTransactionActions={false}
        allowRemoveBatchOperation
        removingBatchOperationId={removingOperationId}
        onRemoveBatchOperation={handleRemoveOperation}
      />
    </SplitsDialogContent>
  )
}
